import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import LabelField from "../Form/FormFields/LabelField";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import Accordion from "../ui/Accordion";
import { barChartConfigGenerator } from "./ChartConfigGenerator";
import NoteComponent from "../ui/table/Notes";

const DynamicBarChart: React.FC<any> = ({ chartListData, width }) => {
  const selectorData = useSelector((state: any) => state?.dynamicData);
  const notes = chartListData?.note;
  const noteData = chartListData?.remarks;

  const chartData = selectorData[chartListData?.id] || [];
  const isLoading = selectorData.loaders[`report/chart/${chartListData?.id}`];

  const isChartReady =
    chartData &&
    Array.isArray(chartData?.label_name) &&
    chartData?.label_name?.length > 0 &&
    Array.isArray(chartData?.data) &&
    chartData?.data?.length > 0;

  const {
    series,
    categories,
    min,
    max,
    xAxisTitle,
    yAxisTitle,
    colors,
    format,
  } = useMemo(() => {
    if (!isChartReady)
      return {
        series: [],
        categories: [],
        min: 0,
        max: 100,
        xAxisTitle: "",
        yAxisTitle: "",
        colors: [],
        format: "",
      };
    return barChartConfigGenerator(chartData.data, chartData.label_name);
  }, [isChartReady, chartData]);

  const formatValue = (val: number) =>
    format === "percentage" ? `${Math.round(val)}%` : val.toLocaleString();

  const chartOptions: any = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        horizontal: false,
        columnWidth: "40%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
      formatter: (val: number) => formatValue(val),
      style: { fontSize: "12px", fontWeight: "bold" },
    },
    xaxis: {
      categories,
      title: { text: xAxisTitle, style: { fontWeight: 600 } },
      labels: { rotate: -45, style: { fontSize: "12px", colors: "#333" } },
    },
    yaxis: {
      min,
      max,
      title: { text: yAxisTitle },
      labels: { formatter: (val: number) => formatValue(val) },
      tickAmount: 5,
      forceNiceScale: true,
      decimalsInFloat: 0,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val: number) => formatValue(val) },
    },
    legend: { show: false },
    fill: {
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.9,
        gradientToColors: ["#3B82F5"],
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    colors,
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 300 },
          xaxis: { labels: { rotate: -30 } },
        },
      },
    ],
  };

  return (
    <div className={`grid gap-2 ${width && width}`}>
      <div className="bg-white p-4 rounded-lg w-full">
        <h2 className="text-lg font-semibold mb-2">
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center">
              <div
                className="text-[#464E5F] text-[14px] mb-0 pb-0 font-semibold"
                style={{ marginBottom: "0 !important" }}
              >
                <LabelField label={chartListData?.title} />
              </div>
              {notes && (
                <NoteComponent
                  notes={noteData}
                  modalTitle={chartListData?.title}
                />
              )}
            </div>
          </div>
        </h2>

        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <div>
            <div className="w-full items-center" style={{ height: 295 }}>
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="bar"
                height="100%"
              />
            </div>

            <Accordion
              title="AI Insights"
              componentID={chartListData?.id}
              chartData={chartListData}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicBarChart;
